import React, { useState } from 'react';
import { Modal } from '../common/Modal';
import { FileUploadZone } from './upload/FileUploadZone';
import { ValidationErrors } from './upload/ValidationErrors';
import { WarningMessage } from './upload/WarningMessage';
import { useAccommodationStore } from '../../stores/accommodationStore';
import { Download, Upload } from 'lucide-react';
import * as XLSX from 'xlsx';
import toast from 'react-hot-toast';
import { Room } from '../../types/accommodation';
import { useLanguage } from '../../contexts/LanguageContext';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  tableId: string;
  currentRooms: Room[];
};

type RoomEditRow = {
  original_room_number: number | string;
  room_type: 'double' | 'triple' | 'quadruple';
  edited_room_number: number | string | null;
  is_empty_room: 'yes' | 'no'; // Added indicator for empty rooms
};

export const RoomBulkEditModal = ({ isOpen, onClose, tableId, currentRooms }: Props) => {
  const { translations } = useLanguage();
  const [file, setFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const { updateRooms } = useAccommodationStore();

  const handleDownloadTemplate = () => {
    // Create template data from current rooms with empty room indicator
    const template: RoomEditRow[] = currentRooms.map(room => ({
      original_room_number: String(room.n),
      room_type: room.t === 'd' ? 'double' : room.t === 't' ? 'triple' : 'quadruple',
      edited_room_number: String(room.n),
      is_empty_room: room.o === 0 ? 'yes' : 'no' // Add empty room indicator
    }));

    // Create worksheet
    const ws = XLSX.utils.json_to_sheet(template);
    
    // Add column headers with descriptions
    XLSX.utils.sheet_add_aoa(ws, [
      [
        'original_room_number', 
        'room_type', 
        'edited_room_number', 
        'is_empty_room'
      ]
    ], { origin: 'A1' });

    // Set column widths
    ws['!cols'] = [
      { wch: 20 }, // original_room_number
      { wch: 30 }, // room_type
      { wch: 30 }, // edited_room_number
      { wch: 30 }, // is_empty_room
    ];
    
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Template');

    // Save file
    XLSX.writeFile(wb, 'room-edit-template.xlsx');
  };

  const validateRoomChanges = (editedRooms: RoomEditRow[]): string[] => {
    const errors: string[] = [];
    const newRoomNumbers = new Set<string>();

    editedRooms.forEach((room, index) => {
      // Skip validation for rows marked for deletion (edited_room_number is empty/null)
      if (room.edited_room_number === null || room.edited_room_number === undefined) {
        // Check if the room is occupied before allowing deletion
        const originalRoomNumberStr = String(room.original_room_number);
        const originalRoom = currentRooms.find(r => String(r.n) === originalRoomNumberStr);
        
        if (originalRoom && originalRoom.o > 0) {
          errors.push(
            translations?.roomBulkEditModal?.occupiedRoomDeleteError ||
            `Cannot delete room ${originalRoomNumberStr} because it is occupied. Please remove all pilgrims first.`
          );
        }
        return;
      }

      // Convert to string for consistency
      const editedRoomNumberStr = String(room.edited_room_number);

      // Check for duplicate new room numbers
      if (newRoomNumbers.has(editedRoomNumberStr)) {
        errors.push(
          translations?.roomBulkEditModal?.duplicateRoomError ||
          `Room number ${editedRoomNumberStr} is duplicated in row ${index + 1}`
        );
      }
      newRoomNumbers.add(editedRoomNumberStr);

      // Validate room type changes for existing rooms
      if (room.original_room_number) {
        const originalRoomNumberStr = String(room.original_room_number);
        const originalRoom = currentRooms.find(r => String(r.n) === originalRoomNumberStr);
        
        const originalType = originalRoom?.t === 'd' ? 'double' : originalRoom?.t === 't' ? 'triple' : 'quadruple';
        if (originalRoom && originalType !== room.room_type && originalRoom.o > 0) {
          errors.push(
            translations?.roomBulkEditModal?.roomTypeChangeError ||
            `Cannot change room type for room ${originalRoomNumberStr} because it is occupied`
          );
        }
      }
    });

    return errors;
  };

  const handleUpload = async () => {
    if (!file) return;

    setIsLoading(true);
    setValidationErrors([]);

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = e.target?.result;
          const workbook = XLSX.read(data, { type: 'binary' });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];
          const rows = XLSX.utils.sheet_to_json(worksheet) as RoomEditRow[];

          // Validate changes
          const errors = validateRoomChanges(rows);
          if (errors.length > 0) {
            setValidationErrors(errors);
            setIsLoading(false);
            return;
          }

          // Process room changes
          const updatedRooms: Room[] = [];

          // First, handle updates and deletions of existing rooms
          currentRooms.forEach(currentRoom => {
            const currentRoomStr = String(currentRoom.n);
            const editRow = rows.find(row => String(row.original_room_number) === currentRoomStr);
            
            // Skip this room if it's marked for deletion (edited_room_number is empty)
            if (editRow && (editRow.edited_room_number === null || editRow.edited_room_number === undefined)) {
              // Only delete if the room is empty
              if (currentRoom.o === 0) {
                return; // This effectively deletes the room
              } else {
                // If room is occupied, keep it (don't delete)
                updatedRooms.push(currentRoom);
                return;
              }
            }

            if (editRow) {
              // Update existing room
              const newType = editRow.room_type === 'double' ? 'd' : editRow.room_type === 'triple' ? 't' : 'q';
              const newCapacity = editRow.room_type === 'double' ? 2 : editRow.room_type === 'triple' ? 3 : 4;
              const newBeds = Array(newCapacity).fill(null).map((_, i) => {
                // Preserve existing bed data if available
                return i < currentRoom.b.length ? currentRoom.b[i] : {
                  a: []
                };
              });

              updatedRooms.push({
                ...currentRoom,
                n: String(editRow.edited_room_number), // Ensure room number is a string
                t: newType,
                c: newCapacity,
                b: newBeds
              });
            } else {
              // Keep unchanged room
              updatedRooms.push(currentRoom);
            }
          });

          // Then, handle new rooms
          rows.forEach(row => {
            if (!row.original_room_number && row.edited_room_number) {
              // Add new room
              const type = row.room_type === 'double' ? 'd' : row.room_type === 'triple' ? 't' : 'q';
              const capacity = row.room_type === 'double' ? 2 : row.room_type === 'triple' ? 3 : 4;
              updatedRooms.push({
                n: String(row.edited_room_number), // Ensure room number is a string
                t: type,
                c: capacity,
                o: 0,
                b: Array(capacity).fill(null).map(() => ({
                  a: []
                }))
              });
            }
          });

          // Update rooms in store
          await updateRooms(tableId, updatedRooms);
          toast.success(
            translations?.roomBulkEditModal?.successMessage ||
            'Rooms updated successfully'
          );
          onClose();
        } catch (error) {
          console.error('Error processing file:', error);
          setValidationErrors([
            translations?.roomBulkEditModal?.processingError ||
            'An error occurred while processing the file'
          ]);
        }
      };

      reader.onerror = () => {
        setValidationErrors([
          translations?.roomBulkEditModal?.fileReadError ||
          'Failed to read file'
        ]);
      };

      reader.readAsBinaryString(file);
    } catch (error: any) {
      console.error('Error uploading file:', error);
      setValidationErrors([
        error.message ||
        translations?.roomBulkEditModal?.uploadError ||
        'An error occurred while uploading the file'
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={translations?.roomBulkEditModal?.title || 'Edit Rooms'}>
      <div className="space-y-6">
        {/* Template Download Button */}
        <button
          onClick={handleDownloadTemplate}
          className="flex items-center gap-2 text-primary-600 hover:text-primary-700"
        >
          <Download size={20} />
          <span>
            {translations?.roomBulkEditModal?.downloadTemplate || 'Download Excel Template'}
          </span>
        </button>

        {/* File Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {translations?.roomBulkEditModal?.fileLabel || 'Excel File'}
            <span className="text-red-500 mr-1">*</span>
          </label>
          <FileUploadZone
            file={file}
            onFileSelect={setFile}
            onFileRemove={() => {
              setFile(null);
              setValidationErrors([]);
            }}
          />
        </div>

        <ValidationErrors errors={validationErrors} />
        <WarningMessage />

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            {translations?.roomBulkEditModal?.cancel || 'Cancel'}
          </button>
          <button
            onClick={handleUpload}
            disabled={isLoading || !file}
            className={`flex items-center gap-2 px-6 py-2 rounded-lg text-white
              ${!file || isLoading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-primary-500 hover:bg-primary-600'}`}
          >
            <Upload size={20} />
            <span>
              {isLoading
                ? translations?.roomBulkEditModal?.updating || 'Updating...'
                : translations?.roomBulkEditModal?.updateRooms || 'Update Rooms'}
            </span>
          </button>
        </div>
      </div>
    </Modal>
  );
};